import { LabelWithoutRef } from '../../../../label/label-without-ref';

import { cn } from '../../../../../utils/cn';

import { WidthIcon } from '@radix-ui/react-icons';
import { SelectModel } from '../../../../select/select-model';
import { InputNumberWithSteps } from '../controls/input-number-with-steps';
import { COLUMNS_COUNT } from './constants';
import type { ColumnsCount } from './types';

const GAP_MIN_SIZE = 0;
const GAP_MAX_SIZE = 96;
const GAP_SIZE_STEPS = [GAP_MIN_SIZE, 16, 32, 48, 64, 80, GAP_MAX_SIZE];
const COLUMN_WIDTH_MIN_SIZE = 16;
const COLUMN_WIDTH_MAX_SIZE = 600;
const COLUMN_WIDTH_SIZE_STEPS = [
  COLUMN_WIDTH_MIN_SIZE,
  100,
  200,
  300,
  400,
  500,
  COLUMN_WIDTH_MAX_SIZE,
];

type DistributionProps = {
  className?: string;
  column1Width: string;
  column2Width: string;
  column3Width: string;
  columnsCount: ColumnsCount;
  columnsGap: string;
  onColumn1WidthChange: (value: string) => void;
  onColumn2WidthChange: (value: string) => void;
  onColumn3WidthChange: (value: string) => void;
  onColumnsCountChange: (value: ColumnsCount) => void;
  onColumnsGapChange: (value: string) => void;
};

export function Distribution({
  className,
  column1Width,
  column2Width,
  column3Width,
  columnsCount,
  columnsGap,
  onColumn1WidthChange,
  onColumn2WidthChange,
  onColumn3WidthChange,
  onColumnsCountChange,
  onColumnsGapChange,
}: DistributionProps) {
  return (
    <div className={cn('space-y-2', className)}>
      <p className="text-xs font-medium">Distribución</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <div>
          <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
            Columnas
          </LabelWithoutRef>
          <SelectModel
            model={COLUMNS_COUNT.map((count) => ({ value: String(count), item: String(count) }))}
            size="sm"
            value={String(columnsCount)}
            variant="outline"
            onValueChange={(value) => onColumnsCountChange(Number(value) as ColumnsCount)}
          />
        </div>
        <div>
          <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
            Espacio entre columnas
          </LabelWithoutRef>
          <InputNumberWithSteps
            id="distribution-columns-gap"
            max={GAP_MAX_SIZE}
            min={GAP_MIN_SIZE}
            steps={GAP_SIZE_STEPS}
            value={columnsGap === '' ? null : Number(columnsGap)}
            onChange={(value) => onColumnsGapChange(value === null ? '' : String(value))}
          />
        </div>
        <div>
          <div className="flex items-center gap-1">
            <WidthIcon className="size-4" />
            <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
              Ancho Columna 1
            </LabelWithoutRef>
          </div>
          <InputNumberWithSteps
            id="distribution-column1-width"
            max={COLUMN_WIDTH_MAX_SIZE}
            min={COLUMN_WIDTH_MIN_SIZE}
            placeholder="Auto"
            steps={COLUMN_WIDTH_SIZE_STEPS}
            value={column1Width === '' ? null : Number(column1Width)}
            onChange={(value) => onColumn1WidthChange(value === null ? '' : String(value))}
          />
        </div>
        <div>
          <div className="flex items-center gap-1">
            <WidthIcon className="size-4" />
            <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
              Ancho Columna 2
            </LabelWithoutRef>
          </div>
          <InputNumberWithSteps
            id="distribution-column2-width"
            max={COLUMN_WIDTH_MAX_SIZE}
            min={COLUMN_WIDTH_MIN_SIZE}
            placeholder="Auto"
            steps={COLUMN_WIDTH_SIZE_STEPS}
            value={column2Width === '' ? null : Number(column2Width)}
            onChange={(value) => onColumn2WidthChange(value === null ? '' : String(value))}
          />
        </div>
        {columnsCount === 3 ? (
          <div>
            <div className="flex items-center gap-1">
              <WidthIcon className="size-4" />
              <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
                Ancho Columna 3
              </LabelWithoutRef>
            </div>
            <InputNumberWithSteps
              id="distribution-column3-width"
              max={COLUMN_WIDTH_MAX_SIZE}
              min={COLUMN_WIDTH_MIN_SIZE}
              placeholder="Auto"
              steps={COLUMN_WIDTH_SIZE_STEPS}
              value={column3Width === '' ? null : Number(column3Width)}
              onChange={(value) => onColumn3WidthChange(value === null ? '' : String(value))}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
